import type {
  CalendarInsightsViewKey,
  CalendarInsightsViewState,
  InsightCategoryKey,
} from './types';

const CATALOGS: Record<
  CalendarInsightsViewKey,
  Record<InsightCategoryKey, { key: string; label: string; on: boolean }[]>
> = {
  daily: {
    revenue: [
      { key: 'overall_day_flow', label: 'Overall Day Business & Flow', on: true },
      { key: 'provider_workload', label: 'Provider Workload Distribution', on: true },
      { key: 'new_patients_today', label: 'New Patients Today', on: true },
    ],
    retention: [
      {
        key: 'hygiene_recare',
        label: 'Hygiene / Recare Completion Opportunity',
        on: false,
      },
      { key: 'family_overdue', label: 'Family Overdue Booking Opportunity', on: true },
      { key: 'same_day_retention', label: 'Same-Day Retention Nudge', on: false },
    ],
    clinical: [
      { key: 'scheduling_bottlenecks', label: 'Potential Scheduling Bottlenecks', on: true },
      { key: 'emergency_slots', label: 'Emergency or Special-Needs Slots', on: true },
    ],
    rcm: [
      {
        key: 'same_day_upsell',
        label: 'Same-Day / Family Upsell Prep',
        on: false,
      },
      { key: 'financial_prep', label: 'Quick Financial Prep Reminder', on: false },
      { key: 'day_end_followup', label: 'Day-End / Follow-Up Focus', on: true },
    ],
  },
  weekly: {
    revenue: [
      { key: 'week_production_proj', label: 'Week Production Projection vs Goal', on: true },
      { key: 'provider_load_balance', label: 'Provider Load Balancing', on: true },
      { key: 'open_chair_hours', label: 'Open Chair Hours Analysis', on: true },
      { key: 'big_cases_week', label: 'Big-Case Treatment Plans This Week', on: true },
    ],
    retention: [
      { key: 'hygiene_reappt_rate', label: 'Hygiene Re-appointment Rate', on: false },
      { key: 'family_chains', label: 'Family Chain Opportunities', on: true },
      { key: 'long_gap_returners', label: 'Long-Gap Returner Re-engagement', on: true },
    ],
    clinical: [
      { key: 'lab_case_deadlines', label: 'Lab Case Ordering Deadlines', on: true },
      { key: 'pre_auth_pending', label: 'Pre-Auth Pending Follow-ups', on: true },
      { key: 'pediatric_consent', label: 'Pediatric Consent Verifications', on: true },
      { key: 'forms_not_sent', label: 'Patients Arriving Without Forms', on: true },
    ],
    rcm: [
      { key: 'expiring_benefits', label: 'Patients with Benefits Expiring', on: true },
      { key: 'deductible_met', label: 'Deductible Met — Schedule Pending', on: false },
      { key: 'weekly_collections', label: 'Weekly Collections Forecast', on: true },
    ],
  },
  monthly: {
    revenue: [
      { key: 'production_gap', label: 'Production Gap to Monthly Goal', on: true },
      { key: 'daily_avg_needed', label: 'Daily Average Needed to Close Gap', on: true },
      { key: 'provider_month_attain', label: 'Per-Provider Month Goal Attainment', on: true },
    ],
    retention: [
      { key: 'hygiene_reappt_vs_goal', label: 'Hygiene Re-appointment Rate vs Goal', on: true },
      { key: 'unscheduled_tx_total', label: 'Total Unscheduled Treatment Plan $', on: true },
      { key: 'patient_birthdays', label: 'Patient Birthdays & Milestones', on: true },
      {
        key: 'referrals_not_contacted',
        label: 'Patient Referrals Not Yet Contacted',
        on: true,
      },
    ],
    clinical: [
      { key: 'license_renewals', label: 'License Renewals Due This Month', on: true },
      { key: 'osha_log', label: 'OSHA Log Review Reminder', on: true },
      { key: 'compliance_deadlines', label: 'Other Compliance Deadlines', on: false },
    ],
    rcm: [
      { key: 'aging_ar', label: 'Aging A/R Review', on: true },
      { key: 'ytd_unused_benefits', label: 'Patients with Unused YTD Benefits', on: true },
      { key: 'insurance_writeoffs', label: 'Insurance Write-offs Analysis', on: false },
      { key: 'unsigned_tx_plans', label: 'Unsigned Treatment Plans by $', on: true },
    ],
  },
};

export function buildInsightCategories(view: CalendarInsightsViewKey) {
  const catalog = CATALOGS[view];
  return {
    revenue: catalog.revenue.map((item) => ({ ...item })),
    retention: catalog.retention.map((item) => ({ ...item })),
    clinical: catalog.clinical.map((item) => ({ ...item })),
    rcm: catalog.rcm.map((item) => ({ ...item })),
  };
}

export const CALENDAR_INSIGHTS_INITIAL: Record<CalendarInsightsViewKey, CalendarInsightsViewState> = {
  daily: {
    guideline:
      "Surface what a seasoned colleague would flag before the day starts. Prioritize patient-readiness blockers (forms, eligibility, pre-auths) over money items. Always include any patient who hasn't been in over 2 years even if there's nothing pressing.",
    lastAnalyzed: 'May 24, 2026 · 9:42 AM',
    categories: buildInsightCategories('daily'),
    rules: [
      {
        id: 'dr_1',
        quote: 'Surface patient-readiness blockers before money items',
        mapsTo: 'Daily tier order · Blockers above Opportunities',
        enabled: true,
      },
      {
        id: 'dr_2',
        quote: 'Always include patients with 2+ year gaps',
        mapsTo: 'Patient Retention & Growth › Long-gap returner check',
        enabled: true,
      },
      {
        id: 'dr_3',
        quote: 'First thing patients hear when they arrive',
        enabled: false,
        unclear: true,
      },
    ],
  },
  weekly: {
    guideline:
      "Focus on what we still have time to act on. Lab cases and pre-auths are the biggest weekly risk — surface them by Wednesday at the latest. Highlight big-case treatment plans where the patient has shown interest but hasn't booked yet.",
    lastAnalyzed: 'May 22, 2026 · 10:08 AM',
    categories: buildInsightCategories('weekly'),
    rules: [
      {
        id: 'wr_1',
        quote: 'Surface lab cases by mid-week',
        mapsTo: 'Clinical Risk & Operations › Lab Case Ordering Deadlines',
        enabled: true,
      },
      {
        id: 'wr_2',
        quote: 'Highlight big-case TPs where patient is interested',
        mapsTo: 'Revenue & Schedule Optimization › Big-Case Treatment Plans',
        enabled: true,
      },
    ],
  },
  monthly: {
    guideline:
      'End-of-month focus: production gap, hygiene reappointment rate, and unscheduled treatment. Surface compliance deadlines (license renewals, OSHA) at least 14 days before they\'re due so we have time to react.',
    lastAnalyzed: 'May 19, 2026 · 8:51 AM',
    categories: buildInsightCategories('monthly'),
    rules: [
      {
        id: 'mr_1',
        quote: 'Surface compliance deadlines 14+ days before due',
        mapsTo: 'Clinical Risk & Operations › License Renewals + OSHA Log',
        enabled: true,
      },
      {
        id: 'mr_2',
        quote: 'Focus on hygiene reappointment rate vs goal',
        mapsTo: 'Patient Retention & Growth › Hygiene Re-appointment Rate',
        enabled: true,
      },
      {
        id: 'mr_3',
        quote: 'Track unsigned tx plans by total dollar value',
        mapsTo: 'Financial & RCM › Unsigned Treatment Plans by $',
        enabled: true,
      },
    ],
  },
};
